import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { getClientCourse } from "../../api/clientCoursesApi";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { formatClientDateTime, formatStatusLabel } from "../../utils/clientPortalFormat";

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="ccf-publish-meta">
      <div>
        <dt>{label}</dt>
        <dd>{value}</dd>
      </div>
    </div>
  );
}

const ClientCourseDetailPage = () => {
  const { clientViewId, courseId } = useParams();
  const [course, setCourse] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const paths = useClientPortalPaths();

  useEffect(() => {
    getClientCourse(clientViewId, courseId)
      .then((body) => {
        setCourse(body.course);
        setCanManage(body.canManage);
      })
      .catch((error) => {
        setErrorMessage(error.message || "Failed to load course.");
      })
      .finally(() => setLoading(false));
  }, [clientViewId, courseId]);

  if (loading) {
    return <ClientPortalLoading message="Loading course\u2026" />;
  }

  if (errorMessage) {
    return <ClientPortalError message={errorMessage} />;
  }

  if (canManage) {
    return <Navigate to={paths.viewSection(clientViewId, "courses", courseId, "edit")} replace />;
  }

  if (!course) {
    return <ClientPortalError message="Course not found." />;
  }

  const location = [course.city, course.stateRegion].filter(Boolean).join(", ");

  return (
    <div className="ccf-layout" style={{ maxWidth: 860 }}>
      <header className="ccf-header">
        <div className="ccf-header__left">
          <nav className="ccf-breadcrumb">
            <Link to={paths.viewSection(clientViewId, "courses")}>Courses</Link>
            <span className="ccf-breadcrumb__sep" aria-hidden="true">/</span>
            <span>{course.courseName}</span>
          </nav>
          <h1 className="ccf-header__title">{course.courseName}</h1>
          {course.slug && (
            <p className="ccf-header__subtitle">/{course.slug}</p>
          )}
        </div>
      </header>

      {course.heroImageUrl && (
        <div className="ccf-image-preview" style={{ marginBottom: 20 }}>
          <img
            src={course.heroImageUrl}
            alt={course.courseName}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}

      <section className="ccf-card">
        <h2 className="ccf-card__title">Overview</h2>
        <div className="ccf-card__body">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span className={`ccol-card__badge ccol-card__badge--${(course.displayStatus || "draft").toLowerCase()}`} style={{ position: "static" }}>
              {formatStatusLabel(course.displayStatus)}
            </span>
            {course.isFeatured && (
              <span className="ccol-card__badge ccol-card__badge--featured" style={{ position: "static" }}>Featured</span>
            )}
            {course.publicPrivateType && (
              <span className="ccol-card__meta-chip">{course.publicPrivateType}</span>
            )}
            {course.courseType && (
              <span className="ccol-card__meta-chip">{course.courseType}</span>
            )}
          </div>

          <p style={{ fontSize: 13, color: "var(--cp-muted, #6b7280)" }}>
            Updated {formatClientDateTime(course.updatedAt)}
          </p>

          {location && (
            <p style={{ fontSize: 14, color: "var(--cp-text, #172033)" }}>
              <span aria-hidden="true" style={{ marginRight: 6 }}>&#128205;</span>
              {location}
            </p>
          )}

          {course.communityName && (
            <p style={{ fontSize: 14 }}>
              <strong>Community:</strong> {course.communityName}
            </p>
          )}

          {(course.shortDescription || course.longDescription) && (
            <p style={{ fontSize: 14, lineHeight: 1.6, color: "var(--cp-text, #172033)" }}>
              {course.shortDescription || course.longDescription}
            </p>
          )}
        </div>
      </section>

      {(course.numberOfHoles || course.par || course.yardage || course.designer || course.yearOpened) && (
        <section className="ccf-card" style={{ marginTop: 16 }}>
          <h2 className="ccf-card__title">Course Details</h2>
          <div className="ccf-card__body">
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {course.numberOfHoles && (
                <span className="ccol-card__meta-chip">{course.numberOfHoles} holes</span>
              )}
              {course.par && (
                <span className="ccol-card__meta-chip">Par {course.par}</span>
              )}
              {course.yardage && (
                <span className="ccol-card__meta-chip">{course.yardage} yards</span>
              )}
            </div>
            <DetailRow label="Designer" value={course.designer} />
            <DetailRow label="Year opened" value={course.yearOpened} />
          </div>
        </section>
      )}
    </div>
  );
};

export default ClientCourseDetailPage;
